import * as React from 'react'
import styled, { css } from 'styled-components'
import ReactModal from 'react-modal'
import media from '../../../services/generateMediaQueries'
import ActionButton from '../../ui/ActionButton/ActionButton'
import Icon from '../../ui/Icon/Icon'

import GCashConsentModal from '../../home/GCashConsentModal/GCashConsentModal'
import Spinner from '../../ui/Spinner/Spinner'
import gCashStatus from '../../../services/data/gCashStatus'
import { setGACustomEvent } from '../../../services/analytics/google-analytics'

const StyledReactModal = styled(ReactModal)`
  &:focus {
    outline: 0;
    border: none;
  }
  &.ReactModal__Overlay--after-open {
    background-color: rgba(0, 0, 0, 0.8);
  }
  &.ReactModal__Overlay {
    background-color: rgba(0, 0, 0, 0.8);
    transition: all var(--animation-duration) var(--animation-easing);
  }
  &.ReactModal__Content {
    width: 450px;
    &--after-open {
      opacity: 1;
      transition: opacity var(--animation-duration) var(--animation-easing);
    }

    &--before-close {
      opacity: 0;
      transition: opacity calc(var(--animation-duration) / 2)
        var(--animation-easing);
    }
  }

  ${media.lessThan('small')`
    top: 0;
    right: 0;
    height: 100% !important;
    border-radius: 0;
    max-width: unset;
    max-height: unset;
  `}
`

const ModalContentWrapper = styled.div`
  &:focus {
    outline: 0;
    border: none;
  }
`

const DefaultCloseButton = styled.button`
  position: absolute;
  border: 0;
  top: 15px;
  right: 15px;
  font-size: 25px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.5);
  width: 32px;
  height: 32px;
  padding: 4px;
  border-radius: 100%;
  outline: none;

  ${props =>
    props.zIndex &&
    css`
      z-index: ${props.zIndex + 1};
    `}

  > svg {
    vertical-align: baseline;
  }
`

const ModalContent = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;

  ${media.lessThan('small')`
    margin: 0;
    height: 100%;
    width: 100%;
    border-radius: 0;
  `}
`

const Content = styled.div`
  padding: 3rem 2rem 2rem;
`

const Buttons = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr;

  ${media.lessThan('small')`
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr;
  `}
`

const Title = styled.h3`
  color: var(--color-primary);
  font-size: 1.5rem;
  font-weight: var(--font-weight-boldest);
  line-height: 2rem;
  text-align: center;
  margin: 1rem 0;
  position: relative;
  z-index: 1;
`

const Description = styled.div`
  p {
    font-size: 0.875rem;
    letter-spacing: 0.1px;
    line-height: 1.3125rem;
    margin: 2rem 0;
    padding: 0 1.6875rem;
    text-align: center;
    margin-block-end: 1.3rem;
  }
`

const StyledActionButton = styled(ActionButton)`
  padding: 0;
  span {
    font-weight: var(--font-weight-boldest);
  }
`
const DifferentNumber = styled.span`
  color: var(--color-primary);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
`

const MobileNumber = styled.span`
  color: var(--color-primary);
  font-weight: var(--font-weight-bold);
  ${props => props.onClick && 'cursor:pointer;'}
`
const Error = styled.p`
  padding: 0.59375rem 1rem;
  position: relative;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  transition-property: padding, margin;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);
  overflow: hidden;

  &::before {
    content: ' ';
    display: block;
    border-radius: var(--border-radius-form);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--color-error);
    opacity: 0.1;
  }

  &:empty {
    padding-top: 0;
    padding-bottom: 0;
    margin: 0;
  }
`

const content = {}

const overlay = {
  zIndex: 10000,
  background: 'rgba(0, 0, 0, 0.8)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}

const StyledConsentModal = styled(GCashConsentModal)`
  color: var(--color-primary);
`

const GCashModal = ({
  show,
  zIndex,
  gCashData,
  mobileNumber,
  onClose,
  onLink,
  onUnLink,
  onInvalid,
  onChangeNumber: onUpdateNumber,
  disableOverlayClick,
  onLoad,
  registration,
  errorMessage,
}) => {
  const [componentHeight, setComponentHeight] = React.useState(365)
  const [loading, setLoading] = React.useState(false)

  const contentRef = React.useCallback(node => {
    if (node !== null) {
      setComponentHeight(node.getBoundingClientRect().height)
    }
  }, [])

  React.useEffect(() => {
    if (registration && onLoad) onLoad()
  }, [registration, onLoad])

  const onProceed = () => {
    setLoading(true)
    try {
      if (gCashData.status === gCashStatus.noRequest) onLink()
      else if (gCashData.status === gCashStatus.enrolled) onUnLink()
      else onClose()
    } catch {}
    setLoading(false)
  }
  const onChangeNumber = async () => {
    onClose()
    onUpdateNumber && onUpdateNumber()
  }
  return (
    <StyledReactModal
      isOpen={show}
      style={{
        overlay: {
          ...overlay,
          ...(zIndex ? { zIndex } : {}),
        },
        content,
      }}
      height={componentHeight}
      onRequestClose={onClose}
      closeTimeoutMS={150}
      shouldCloseOnOverlayClick={!disableOverlayClick}
      bodyOpenClassName="sticky-modal-open"
      htmlOpenClassName="sticky-modal-open"
    >
      <ModalContentWrapper ref={contentRef}>
        <ModalContent>
          <DefaultCloseButton zIndex={zIndex} onClick={onClose}>
            <Icon name="x" />
          </DefaultCloseButton>
          <Content>
            <Error>{errorMessage}</Error>
            {!errorMessage ? (
              <>
                <Title>
                  {gCashData.status === gCashStatus.noRequest &&
                    'Start earning Zing Points using your GCash'}
                  {(gCashData.status === gCashStatus.enrolled ||
                    gCashData.status === gCashStatus.verified) &&
                    'Are you sure you want to unlink your Gcash Account?'}
                </Title>
                <Description>
                  {gCashData.status === gCashStatus.noRequest && (
                    <>
                      <p>
                        <MobileNumber>{mobileNumber}</MobileNumber>
                      </p>
                      <p>
                        If this number has a GCash membership, we will use this
                        to link it to your Zing account.
                        {!registration && (
                          <span>
                            If not,&nbsp;
                            <DifferentNumber onClick={onChangeNumber}>
                              use a different number
                            </DifferentNumber>
                            .
                          </span>
                        )}
                      </p>
                      <p>
                        Validation may take 24 to 48 hours. We will send a
                        notification once validation has been completed and your
                        GCash account has been linked.
                      </p>
                      <p>
                        By adding and linking your GCash account, you agree to
                        the program&nbsp;
                        <StyledConsentModal
                          onClick={() =>
                            registration &&
                            setGACustomEvent('screen_view', {
                              screen_name:
                                'Registration - GCash Popup - Consent',
                            })
                          }
                          zIndex={10022}
                        >
                          GCash Consent and Transaction Monitoring
                        </StyledConsentModal>
                      </p>
                    </>
                  )}
                  {gCashData.status === gCashStatus.enrolled && (
                    <>
                      <p>
                        You are about to unlink this mobile number&nbsp;
                        <MobileNumber>{mobileNumber}</MobileNumber>. Once
                        unlinked, you will stop earning Zing Points from this
                        GCash account. Are you sure you want to proceed?
                      </p>
                    </>
                  )}

                  {gCashData.status === gCashStatus.invalid && (
                    <>
                      <p>
                        Sorry, but this number&nbsp;
                        <MobileNumber>{mobileNumber}</MobileNumber> doesn’t have
                        a GCash account. You may do the following:
                        <br />
                        Use another number by updating your profile or Register
                        this number to GCash
                        <DifferentNumber onClick={onInvalid}>
                          {' Register this number to GCash'}
                        </DifferentNumber>
                      </p>
                    </>
                  )}
                </Description>
              </>
            ) : null}
            {errorMessage ? (
              <>
                <ActionButton
                  variant="primary"
                  onClick={onClose}
                  style={{ width: '100%' }}
                >
                  Okay
                </ActionButton>
              </>
            ) : (
              <Buttons>
                <StyledActionButton
                  variant="primary"
                  onClick={onProceed}
                  disabled={loading}
                >
                  {loading ? <Spinner /> : 'Proceed'}
                </StyledActionButton>
                <StyledActionButton onClick={onClose} disabled={loading}>
                  {loading ? <Spinner /> : 'Not Now'}
                </StyledActionButton>
              </Buttons>
            )}
          </Content>
        </ModalContent>
      </ModalContentWrapper>
    </StyledReactModal>
  )
}
export default GCashModal
